import React, { useMemo, useState } from 'react';
import { Loader2, FileDown, Info } from 'lucide-react';
import HourClock from './HourClock';
import ExecutiveSummary from './ExecutiveSummary';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * BroadcastClockView — multi-clock viewer.
 *
 * Renders one HourClock per active hour for the selected date. The
 * hour range is user-controlled (defaults to the first active hour
 * of the day so the page never opens blank). Includes a "PDF
 * Export" button that downloads a branded report covering the
 * selected range.
 *
 * Props mirror BroadcastTimeline so they can share the same parent:
 *   stationId : string
 *   token     : string
 *   date      : 'YYYY-MM-DD'
 *   events    : array of enriched timeline events (music + gap)
 *   minGapSeconds : int — passed through to the PDF export call
 */

const API = process.env.REACT_APP_BACKEND_URL;

const SONG_HEX = '#16a34a';
// v2.13.21 (2026-02): 4-colour content_type palette.
const BREAK_HEX           = '#f59e0b'; // amber-500
const CONTENT_BLOCK_HEX   = '#3b82f6'; // blue-500
const PROGRAM_SEGMENT_HEX = '#a855f7'; // purple-500
const UNKNOWN_HEX         = '#4b5563'; // slate-600
const NO_DATA_HEX         = UNKNOWN_HEX;

const BroadcastClockView = ({
  stationId,
  token,
  date,
  events,
  summary,
  listenerCurve,
  listenerCurveYesterday,
  minGapSeconds = 30,
}) => {
  const { t } = useLanguage();
  const [exporting, setExporting] = useState(false);
  const [pdfError, setPdfError] = useState(null);

  // v2.13.72 — Group events by hour, BUT cross-hour gaps must
  // appear in EVERY hour they span (clipped by HourClock). The
  // previous version pushed the event only into the start-hour
  // bucket, so a gap from 09:58 → 10:04 would show as 2 min in
  // hour 9's clock and DISAPPEAR from hour 10's clock entirely.
  // Operator complained on 2026-06-28 that "NO DATA" segments
  // appeared inside breaks because the second half was missing.
  //
  // For non-gap events (music) we keep the original start-hour-only
  // behaviour — songs don't visually need to repeat across hours.
  const eventsByHour = useMemo(() => {
    const map = {};
    (events || []).forEach((ev) => {
      const startDt = new Date(ev.start);
      if (Number.isNaN(startDt.getTime())) return;
      const startHour = startDt.getHours();

      // Compute end-hour. Prefer the explicit `end` field added by
      // the backend in v2.13.72; fall back to start + duration.
      let endDt;
      if (ev.end) {
        endDt = new Date(ev.end);
      } else if (ev.duration_seconds && Number.isFinite(ev.duration_seconds)) {
        endDt = new Date(startDt.getTime() + ev.duration_seconds * 1000);
      } else {
        endDt = startDt;
      }
      const endHour = endDt.getHours();
      const endDay = endDt.getDate();
      const startDay = startDt.getDate();

      // Push into start-hour bucket unconditionally.
      if (!map[startHour]) map[startHour] = [];
      map[startHour].push(ev);

      // For GAP events that span >1 hour, also push into every
      // intermediate hour and the end hour, so HourClock renders
      // the clipped portion in each. (HourClock already does the
      // Math.max(0, …) / Math.min(3600, …) clipping correctly.)
      if (ev.type !== 'gap') return;
      if (startHour === endHour && startDay === endDay) return;

      // Walk hour-by-hour from startHour+1 to endHour (within the
      // same day; broadcast day is 24 h, never longer).
      let cursor = new Date(startDt);
      cursor.setMinutes(0, 0, 0);
      cursor.setHours(cursor.getHours() + 1);
      while (cursor <= endDt) {
        const h = cursor.getHours();
        // Stop after 24 iterations as a sanity guard.
        if (!map[h]) map[h] = [];
        map[h].push(ev);
        cursor = new Date(cursor.getTime() + 60 * 60 * 1000);
      }
    });
    return map;
  }, [events]);

  const activeHours = useMemo(
    () =>
      Object.keys(eventsByHour)
        .map((k) => parseInt(k, 10))
        .sort((a, b) => a - b),
    [eventsByHour],
  );

  // Default range = first active hour. Once the user picks a value
  // it's preserved across re-renders. Date changes reset both ends.
  const [overrideFrom, setOverrideFrom] = useState(null);
  const [overrideTo, setOverrideTo] = useState(null);
  const [lastDate, setLastDate] = useState(date);
  if (date !== lastDate) {
    setLastDate(date);
    setOverrideFrom(null);
    setOverrideTo(null);
  }

  const hourFrom =
    overrideFrom != null
      ? overrideFrom
      : activeHours.length
      ? activeHours[0]
      : null;
  const hourTo =
    overrideTo != null
      ? overrideTo
      : activeHours.length
      ? activeHours[0]
      : null;

  const visibleHours = useMemo(() => {
    if (hourFrom == null || hourTo == null) return [];
    const lo = Math.min(hourFrom, hourTo);
    const hi = Math.max(hourFrom, hourTo);
    return activeHours.filter((h) => h >= lo && h <= hi);
  }, [activeHours, hourFrom, hourTo]);

  const handleExportPdf = () => {
    if (!stationId || !date || hourFrom == null || hourTo == null) return;
    setExporting(true);
    setPdfError(null);
    const lo = Math.min(hourFrom, hourTo);
    const hi = Math.max(hourFrom, hourTo);
    const xhr = new XMLHttpRequest();
    xhr.open(
      'GET',
      `${API}/api/broadcast/clock/${stationId}/pdf?date=${date}&hour_from=${lo}&hour_to=${hi}&min_gap_seconds=${minGapSeconds}`,
    );
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.responseType = 'blob';
    xhr.onload = () => {
      setExporting(false);
      if (xhr.status >= 200 && xhr.status < 300) {
        const cd = xhr.getResponseHeader('Content-Disposition') || '';
        const m = /filename="?([^";]+)"?/.exec(cd);
        const filename = m ? m[1] : `clocks_${date}.pdf`;
        const url = window.URL.createObjectURL(xhr.response);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        setPdfError(t('clkView_pdfError'));
      }
    };
    xhr.onerror = () => {
      setExporting(false);
      setPdfError(t('clkView_pdfError'));
    };
    xhr.send();
  };

  if (!activeHours.length) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-lg p-8 text-center">
        <Info className="w-6 h-6 text-white/30 mx-auto mb-2" />
        <div className="text-sm text-white/60">
          {t('clkView_noActiveHours')}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="broadcast-clock-view">
      {/* Range controls */}
      <div className="flex flex-wrap items-center gap-3">
        <label className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-md px-2.5 py-1.5">
          <span className="text-[10px] text-white/40 uppercase tracking-wide">
            {t('clkView_hourFrom')}
          </span>
          <select
            value={hourFrom ?? activeHours[0]}
            onChange={(e) => setOverrideFrom(parseInt(e.target.value, 10))}
            className="bg-transparent text-[12px] text-white/90 outline-none cursor-pointer"
            data-testid="clock-range-from"
          >
            {activeHours.map((h) => (
              <option key={h} value={h} className="bg-gray-900">
                {String(h).padStart(2, '0')}:00
              </option>
            ))}
          </select>
        </label>
        <label className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-md px-2.5 py-1.5">
          <span className="text-[10px] text-white/40 uppercase tracking-wide">
            {t('clkView_hourTo')}
          </span>
          <select
            value={hourTo ?? activeHours[0]}
            onChange={(e) => setOverrideTo(parseInt(e.target.value, 10))}
            className="bg-transparent text-[12px] text-white/90 outline-none cursor-pointer"
            data-testid="clock-range-to"
          >
            {activeHours.map((h) => (
              <option key={h} value={h} className="bg-gray-900">
                {String(h).padStart(2, '0')}:00
              </option>
            ))}
          </select>
        </label>

        <button
          onClick={handleExportPdf}
          disabled={
            exporting || !stationId || !date || hourFrom == null || hourTo == null
          }
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-medium transition-colors ${
            exporting
              ? 'bg-white/5 text-white/30 cursor-not-allowed'
              : 'bg-amber-500/15 text-amber-300 hover:bg-amber-500/25 border border-amber-500/30'
          }`}
          data-testid="clock-export-pdf-btn"
        >
          {exporting ? (
            <>
              <Loader2 className="w-3 h-3 animate-spin" />{' '}
              {t('clkView_exportingPdf')}
            </>
          ) : (
            <>
              <FileDown className="w-3 h-3" /> {t('clkView_exportPdf')}
            </>
          )}
        </button>

        {pdfError && (
          <span className="text-[11px] text-red-400">{pdfError}</span>
        )}

        <span className="text-[10px] text-white/30 italic ml-auto">
          {t('clkView_dataRetentionHint')}
        </span>
      </div>

      {/* Legend (v2.13.21 — 4-category content_type palette) */}
      <div className="bg-white/5 border border-white/10 rounded-md px-3 py-2 flex flex-wrap gap-x-5 gap-y-1 text-[11px] text-white/70">
        <div className="flex items-center gap-2">
          <span className="font-medium text-white/50 uppercase tracking-wide text-[10px]">
            {t('clkView_legendSongs')}:
          </span>
          <span className="inline-flex items-center gap-1" data-testid="legend-music">
            <span
              className="inline-block w-2.5 h-2.5 rounded-sm"
              style={{ backgroundColor: SONG_HEX }}
            />
            {t('clkView_song')}
          </span>
        </div>
        <span className="inline-flex items-center gap-1" data-testid="legend-break">
          <span
            className="inline-block w-2.5 h-2.5 rounded-sm"
            style={{ backgroundColor: BREAK_HEX }}
          />
          {t('clkView_break')}
        </span>
        <span className="inline-flex items-center gap-1" data-testid="legend-content-block">
          <span
            className="inline-block w-2.5 h-2.5 rounded-sm"
            style={{ backgroundColor: CONTENT_BLOCK_HEX }}
          />
          {t('clkView_contentBlock')}
        </span>
        <span className="inline-flex items-center gap-1" data-testid="legend-program-segment">
          <span
            className="inline-block w-2.5 h-2.5 rounded-sm"
            style={{ backgroundColor: PROGRAM_SEGMENT_HEX }}
          />
          {t('clkView_programSegment')}
        </span>
        <span className="inline-flex items-center gap-1" data-testid="legend-unknown">
          <span
            className="inline-block w-2.5 h-2.5 rounded-sm"
            style={{ backgroundColor: UNKNOWN_HEX }}
          />
          {t('clkView_noData')}
        </span>
        <div className="text-[10px] text-white/40 ml-auto">
          {t('clkView_legendHelp')}
        </div>
      </div>

      {/* Clock grid — single column, full-width, larger clocks */}
      <div className="grid grid-cols-1 gap-8 max-w-7xl mx-auto">
        {visibleHours.map((h) => (
          <HourClock
            key={h}
            hour={h}
            date={date}
            events={eventsByHour[h] || []}
            listenerCurve={listenerCurve}
            listenerCurveYesterday={listenerCurveYesterday}
          />
        ))}
      </div>

      {/* Executive Summary — bottom of the page, after all clocks. */}
      {summary && (
        <ExecutiveSummary
          summary={summary}
          stationId={stationId}
          date={date}
          token={token}
        />
      )}
    </div>
  );
};

export default BroadcastClockView;
